import React from "react";
import { ApiClient,ApiError } from "../api/client";
import type { AuthInfo,AuthRole } from "../api/types";
import { trStatic } from "../i18n/static";
import { getCurrentAuthScope, isAuthPersistent, loadAuthFromStorage, saveAuthToStorage } from "./authStorage";
import type { AuthScope } from "./authStorage";
import { reconcileSession as reconcileSharedSession } from "../../../../../../shared/frontend/system/sessionReconciliation";
import { signalAppRuntimeUnavailable } from "../../../../../../shared/frontend/system/appRuntimeGate";
import { setApiRequestsBlocked } from "../../../../../../shared/frontend/api/apiRequestGate";
import { tenantImportApiScope } from "../api/tenantImportRequestPolicy";
import { hasPermissionIn } from "./permissionMatch";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";
import { HTTP_STATUS_UNAUTHORIZED } from "../constants/httpStatus";
import {disposeOperationSession} from '../../../../../../shared/frontend/operations/operationSession';

const HTTPS_PROTOCOL = "https:";
const REMEMBER_ME_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

function setAuthCookie(token: string, persist = false) {
  try {
    if (typeof document === "undefined") return;
    const isHttps = typeof window !== "undefined" && window.location.protocol === HTTPS_PROTOCOL;
    const secure = isHttps ? "; Secure" : "";
    // In some deployments the UI may be embedded / considered cross-site.
    // Use SameSite=None on HTTPS so the cookie is sent for EventSource reliably.
    const sameSite = isHttps ? "None" : "Lax";
    // Produktspezifischer Name: "authToken" kollidierte auf localhost mit den
    // HttpOnly-Cookies der anderen mQorva-Apps (Cookies gelten pro Host ohne Port).
    if (token) {
      const maxAge = persist ? `; Max-Age=${REMEMBER_ME_COOKIE_MAX_AGE_SECONDS}` : "";
      document.cookie = `m1member_authToken=${encodeURIComponent(token)}; Path=/; SameSite=${sameSite}${secure}${maxAge}`;
    } else {
      // Clear cookie
      document.cookie = `m1member_authToken=; Max-Age=0; Path=/; SameSite=${sameSite}${secure}`;
    }
    // Legacy-Cookie aufraeumen (alter geteilter Name).
    document.cookie = `authToken=; Max-Age=0; Path=/; SameSite=${sameSite}${secure}`;
  } catch {
    // best-effort
  }
}

type AuthState = {
  token: string;
  info: AuthInfo | null;
  isReady: boolean;
};

type AuthContextValue = AuthState & {
  api: ApiClient;
  login: (email: string, password: string, stayLoggedIn?: boolean) => Promise<AuthInfo>;
  logout: () => void;
  refreshMe: (options?: { afterWrite?: boolean }) => Promise<void>;
  reconcileSession: (signal?: AbortSignal) => Promise<void>;
  hasRole: (role: AuthRole) => boolean;
  hasPermission: (perm: string) => boolean;
  hasAnyPermission: (...perms: string[]) => boolean;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider(props: { children: React.ReactNode }) {
  // Aktiver Bereich (Portal vs. Plattform-Admin): bestimmt, welcher Storage-Scope die
  // Sitzung liefert. Beide Sitzungen koennen parallel bestehen; der Cookie-Spiegel
  // (GET-Fallback, z. B. Downloads) folgt immer dem aktiven Bereich.
  const scopeRef = React.useRef<AuthScope>(getCurrentAuthScope());
  const activeApi=React.useRef<ApiClient|null>(null);
  const [state, setState] = React.useState<AuthState>(() => {
    const stored = loadAuthFromStorage(getCurrentAuthScope());
    return { token: stored.token, info: stored.info, isReady: false };
  });

  React.useEffect(() => {
    const onHashChange = () => {
      const nextScope = getCurrentAuthScope();
      if (nextScope === scopeRef.current) return;
      if(activeApi.current)disposeOperationSession(activeApi.current);
      scopeRef.current = nextScope;
      const stored = loadAuthFromStorage(nextScope);
      setAuthCookie(stored.token || "", stored.persist);
      setState((s) => ({ ...s, token: stored.token, info: stored.info }));
      signalAppRuntimeUnavailable();
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const api = React.useMemo(() => {
    const requestScope = getCurrentAuthScope();
    const client = new ApiClient({
      getToken: () => getCurrentAuthScope() === requestScope && loadAuthFromStorage(requestScope).token === state.token ? state.token : "",
      onUnauthorized: () => {
        if (requestScope !== getCurrentAuthScope() || loadAuthFromStorage(requestScope).token !== state.token) return;
        disposeOperationSession(client);
        setState((s) => {
          if (s.token !== state.token || getCurrentAuthScope() !== requestScope) return s;
          const next = { ...s, token: "", info: null };
          setAuthCookie("");
          saveAuthToStorage({ token: "", info: null }, requestScope);
          return next;
        });
      }
    });
    return client;
  }, [state.token]);
  React.useLayoutEffect(()=>{activeApi.current=api;return()=>{disposeOperationSession(api);};},[api]);

  React.useEffect(() => () => api.cancelPendingReads(), [api]);
  React.useLayoutEffect(() => {
    if (state.info?.tenantId && state.info.role !== PLATFORM_ADMIN_ROLE)
      setApiRequestsBlocked(tenantImportApiScope(state.info.tenantId), state.info.tenantImportCommitInProgress === true);
  }, [state.info]);

  const setAuth = React.useCallback((auth: { token: string; info: AuthInfo | null }, persist?: boolean) => {
    const previous=loadAuthFromStorage(scopeRef.current);
    if(activeApi.current&&(previous.token!==auth.token||previous.info?.accountId!==auth.info?.accountId||previous.info?.tenantId!==auth.info?.tenantId))disposeOperationSession(activeApi.current);
    if (auth.info?.tenantId && auth.info.role !== PLATFORM_ADMIN_ROLE)
      setApiRequestsBlocked(tenantImportApiScope(auth.info.tenantId), auth.info.tenantImportCommitInProgress === true);
    const resolvedPersist = persist ?? isAuthPersistent(scopeRef.current);
    setAuthCookie(auth.token || "", resolvedPersist);
    saveAuthToStorage(auth, scopeRef.current, resolvedPersist);
    // Unveränderte /me-Antworten sind keine neue Berechtigungsrevision. Die
    // Objektidentität ist Teil der Query-Keys; sonst starten gesperrte Abfragen
    // bei jedem Refresh neu und erzeugen wiederum weitere /me-Refreshes.
    setState((s) => s.token === auth.token && JSON.stringify(s.info) === JSON.stringify(auth.info)
      ? s
      : { ...s, token: auth.token, info: auth.info });
  }, []);

  const refreshGeneration = React.useRef(0);
  const refreshInFlight = React.useRef<{ api: ApiClient; scope: AuthScope; promise: Promise<void>; afterWrite: boolean } | null>(null);
  const refreshMe = React.useCallback((options?: { afterWrite?: boolean }): Promise<void> => {
    if (!state.token) return Promise.resolve();
    const scope = getCurrentAuthScope();
    const afterWrite = options?.afterWrite === true;
    if (!afterWrite && refreshInFlight.current?.api === api && refreshInFlight.current.scope === scope) {
      return refreshInFlight.current.afterWrite ? refreshInFlight.current.promise.catch(error => {
        if (error instanceof ApiError && error.status === HTTP_STATUS_UNAUTHORIZED) throw error;
      }) : refreshInFlight.current.promise;
    }
    const generation = ++refreshGeneration.current;
    const promise = (async () => {
      try {
        const me = await api.me();
        // Eine alte Antwort darf weder Logout noch Portal-/Adminwechsel rückgängig machen.
        if (generation !== refreshGeneration.current || getCurrentAuthScope() !== scope || loadAuthFromStorage(scope).token !== state.token) {
          if (afterWrite) throw new Error('Account.error.profile');
          return;
        }
        setAuth({ token: state.token, info: { ...me, token: state.token } });
      } catch (e) {
        // Temporäre Fehler lassen den bestehenden Authzustand bestehen; 401 meldet ab.
        if (afterWrite || e instanceof ApiError && e.status === HTTP_STATUS_UNAUTHORIZED) throw e;
      }
    })().finally(() => {
      if (refreshInFlight.current?.promise === promise) refreshInFlight.current = null;
    });
    refreshInFlight.current = { api, scope, promise, afterWrite };
    return promise;
  }, [api, setAuth, state.token]);

  const reconcileSession = async (signal?: AbortSignal) => {
    if (!state.token) { setState(s => ({ ...s, isReady: true })); return; }
    const scope = scopeRef.current;
    await reconcileSharedSession({
      revision: () => `${getCurrentAuthScope()}:${loadAuthFromStorage(scope).token}`,
      load: signal => api.me(signal),
      apply: me => { setAuth({ token: state.token, info: { ...me, token: state.token } }); setState(s => ({ ...s, isReady: true })); },
      clear: () => { setAuth({ token: "", info: null }); setState(s => ({ ...s, isReady: true })); },
      isUnauthorized: error => error instanceof ApiError && error.status === HTTP_STATUS_UNAUTHORIZED,
      isCleared: () => getCurrentAuthScope() === scope && !loadAuthFromStorage(scope).token,
    }, signal);
  };


  const login = React.useCallback(
    async (email: string, password: string, stayLoggedIn = true) => {
      const info = await api.login(email, password, stayLoggedIn);
      if (!info?.token) throw new Error(trStatic("Invalid server response: token missing"));
      // Sitzung landet im Scope, der zur Rolle passt. Bei Cross-Scope-Login (z. B.
      // Plattformkonto auf dem Portal-Login) uebernimmt der Zielbereich die Sitzung
      // beim Navigationswechsel (hashchange laedt den Ziel-Scope).
      const targetScope: AuthScope = info.role === PLATFORM_ADMIN_ROLE ? "admin" : "portal";
      if (targetScope === scopeRef.current) {
        setAuth({ token: info.token, info }, stayLoggedIn);
      } else {
        saveAuthToStorage({ token: info.token, info }, targetScope, stayLoggedIn);
      }
      return info;
    },
    [api, setAuth]
  );

  const logout = React.useCallback(() => {
    setAuth({ token: "", info: null });
  }, [setAuth]);

  const hasRole = React.useCallback(
    (role: AuthRole) => {
      return (state.info?.role || "") === role;
    },
    [state.info?.role]
  );

  const hasPermission = React.useCallback(
    (perm: string) => {
      return hasPermissionIn(state.info?.permissions as string[] | undefined, perm, state.info?.role);
    },
    [state.info?.permissions, state.info?.role]
  );

  const hasAnyPermission = React.useCallback(
    (...perms: string[]) => {
      const role = (state.info?.role || "") as string;
      if (role === PLATFORM_ADMIN_ROLE) return true;
      const list = (state.info?.permissions || []) as string[];
      if (list.includes("*")) return true;
      return perms.some((p) => !!p && hasPermission(p));
    },
    [hasPermission, state.info?.permissions, state.info?.role]
  );

  const value: AuthContextValue = {
    ...state,
    api,
    login,
    logout,
    refreshMe,
    reconcileSession,
    hasRole,
    hasPermission,
    hasAnyPermission
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von AuthProvider verwendet werden.");
  return ctx;
}
